import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { SECTIONS, DECISIONS, TIMELINES } from "../data/questions";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

const SCALE = [1, 2, 3, 4, 5];

function ScaleInput({ q, value, onChange }) {
  return (
    <div className="grid grid-cols-5 gap-2">
      {SCALE.map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            data-testid={`opt-${q.id}-${n}`}
            onClick={() => onChange(n)}
            className={`h-14 rounded-2xl border font-mono text-lg transition-all duration-300 ${
              active ? "border-brand-orange bg-brand-orange/10 text-brand-orange scale-[1.04]" : "border-clay text-stone hover:border-[#DDA15E]"
            }`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

function ChoiceInput({ q, value, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
      {q.options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            data-testid={`opt-${q.id}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={`py-3 px-2 rounded-2xl border text-sm transition-all duration-300 ${
              active ? "border-brand-orange bg-brand-orange/10 text-brand-orange scale-[1.03]" : "border-clay text-stone hover:border-[#DDA15E]"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Survey() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [ctx, setCtx] = useState({ decision: "", timeline: "", importance: 3, risk: 3 });
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = SECTIONS.length + 1;
  const progress = Math.round((step / totalSteps) * 100);

  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const sectionComplete = (section) => section.questions.every((q) => answers[q.id] != null);
  const decisionComplete = ctx.decision && ctx.timeline;

  const next = () => {
    if (step < SECTIONS.length) {
      if (!sectionComplete(SECTIONS[step])) {
        toast.error("Please answer every question to continue.");
        return;
      }
      setStep(step + 1);
      window.scrollTo({ top: 0 });
    }
  };

  const submit = async () => {
    if (!decisionComplete) {
      toast.error("Pick a decision and timeline.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessments", { answers, decision_context: ctx });
      navigate(`/results/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const isDecisionStep = step === SECTIONS.length;
  const section = SECTIONS[step];

  return (
    <div className="min-h-screen grain">
      <Navbar />

      <div className="sticky top-16 z-30 bg-oat/80 backdrop-blur-xl border-b border-clay">
        <div className="max-w-3xl mx-auto px-5 md:px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="label-tiny">Step {step + 1} of {totalSteps}</span>
            <span className="font-mono text-xs text-stone">{progress}%</span>
          </div>
          <div className="h-1.5 bg-clay rounded-full overflow-hidden">
            <motion.div className="h-full rounded-full" style={{ background: "linear-gradient(90deg, #FF4D4D, #FF8A00)" }} animate={{ width: `${progress}%` }} transition={{ ease: [0.22, 1, 0.36, 1] }} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-6 py-10 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            {!isDecisionStep ? (
              <>
                <span className="label-tiny">{section.title} · weight {section.weight}</span>
                <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-2 mb-2 text-ink">{section.title}</h1>
                <p className="text-stone text-sm mb-8">{section.subtitle}</p>

                <div className="space-y-8">
                  {section.questions.map((q, i) => (
                    <div key={q.id} data-testid={`question-${q.id}`} className="card-soft p-6">
                      <div className="flex gap-3 mb-3">
                        <span className="font-mono text-xs text-brand-orange mt-1">Q{i + 1}</span>
                        <p className="text-base text-ink">{q.text}</p>
                      </div>
                      {q.type === "choice" ? (
                        <ChoiceInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                      ) : (
                        <>
                          <ScaleInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                          <div className="flex justify-between mt-2 text-[0.7rem] text-stone/70">
                            <span>{q.low}</span>
                            <span>{q.high}</span>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <span className="label-tiny">Decision context</span>
                <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-2 mb-2 text-ink">What are you weighing?</h1>
                <p className="text-stone text-sm mb-8">This powers your Decision Fit forecast.</p>

                <div className="space-y-8">
                  <div>
                    <label className="label-tiny">The decision</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {DECISIONS.map((d) => (
                        <button key={d.value} type="button" data-testid={`decision-${d.value}`} onClick={() => setCtx({ ...ctx, decision: d.value })}
                          className={`py-3 px-2 rounded-2xl border text-sm transition-all duration-300 ${ctx.decision === d.value ? "border-brand-red bg-brand-red/10 text-brand-red scale-[1.03]" : "border-clay text-stone hover:border-[#DDA15E]"}`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="label-tiny">Timeline</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                      {TIMELINES.map((t) => (
                        <button key={t.value} type="button" data-testid={`timeline-${t.value}`} onClick={() => setCtx({ ...ctx, timeline: t.value })}
                          className={`py-3 px-2 rounded-2xl border text-sm transition-all duration-300 ${ctx.timeline === t.value ? "border-brand-orange bg-brand-orange/10 text-brand-orange scale-[1.03]" : "border-clay text-stone hover:border-[#DDA15E]"}`}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="label-tiny">How important is this decision? ({ctx.importance}/5)</label>
                    <input data-testid="importance-slider" type="range" min="1" max="5" value={ctx.importance} onChange={(e) => setCtx({ ...ctx, importance: Number(e.target.value) })} className="w-full mt-3" style={{ accentColor: "#FF8A00" }} />
                  </div>

                  <div>
                    <label className="label-tiny">How much risk does it carry? ({ctx.risk}/5)</label>
                    <input data-testid="risk-slider" type="range" min="1" max="5" value={ctx.risk} onChange={(e) => setCtx({ ...ctx, risk: Number(e.target.value) })} className="w-full mt-3" style={{ accentColor: "#FF4D4D" }} />
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-12">
          <button data-testid="survey-back" disabled={step === 0} onClick={() => { setStep(step - 1); window.scrollTo({ top: 0 }); }}
            className="inline-flex items-center gap-2 text-sm text-stone hover:text-ink transition-colors duration-300 disabled:opacity-30">
            <ArrowLeft size={16} /> Back
          </button>

          {!isDecisionStep ? (
            <button data-testid="survey-next" onClick={next} className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-ink text-white font-semibold hover:bg-[#413b36] transition-colors duration-300">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="survey-submit" onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300 disabled:opacity-60">
              {submitting ? "Scoring…" : "Reveal my score"} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
